import { useState, useEffect } from "react";
import { Grid2, Alert } from "@mui/material";
import useLanguage from "../../hooks/useLanguage";
import useContent from "../../hooks/useContent";
import useToaster from "../../hooks/useToaster";
import useMediaQuery from "../../hooks/useMediaQuery";
import CommonPage from "../../components/common-page";
import M3U8Player from "./components/M3U8Player";
import TVGuide from "./components/TVGuide";
import RecordDialog from "./components/RecordDialog";
import StreamSkeleton from "./skeleton";
import { israelParts } from "./time";

const MOCK_STREAM_URL = "/out/v1/1a7c55961dec4c72b51d79e5d9216cff/index.m3u8";

export default function Stream() {
  const [
    {
      pages: { stream: text },
    },
  ] = useLanguage();
  const {
    getTVGuide,
    getReshetRecords,
    getRecordingPlan,
    createReshetRecord,
    deleteReshetRecord,
  } = useContent();
  const { addToaster } = useToaster();
  const isMobile = useMediaQuery();

  const [shows, setShows] = useState([]);
  const [selected, setSelected] = useState([]); // RecordingOrder[]
  const [plan, setPlan] = useState([]); // resolved RecordingPlanRow[] (drives highlights)
  const [unresolved, setUnresolved] = useState([]); // recurrings that couldn't match a day
  const [loading, setLoading] = useState(true);
  const [guideUnavailable, setGuideUnavailable] = useState(false);
  const [dialogShow, setDialogShow] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.allSettled([getTVGuide(), getReshetRecords(), getRecordingPlan()])
      .then(([guideRes, recordsRes, planRes]) => {
        const guideOk =
          guideRes.status === "fulfilled" && Array.isArray(guideRes.value);
        setShows(guideOk ? guideRes.value : []);
        setGuideUnavailable(!guideOk);

        if (recordsRes.status === "fulfilled" && Array.isArray(recordsRes.value)) {
          setSelected(recordsRes.value);
        } else {
          setSelected([]);
        }
        const planOk = planRes.status === "fulfilled" && planRes.value;
        setPlan(planOk && Array.isArray(planRes.value.plan) ? planRes.value.plan : []);
        setUnresolved(planOk && Array.isArray(planRes.value.unresolved) ? planRes.value.unresolved : []);
      })
      .finally(() => setLoading(false));
  }, []);

  const refetchSelected = async () => {
    const [recs, planRes] = await Promise.all([getReshetRecords(), getRecordingPlan()]);
    setSelected(Array.isArray(recs) ? recs : []);
    setPlan(Array.isArray(planRes?.plan) ? planRes.plan : []);
    setUnresolved(Array.isArray(planRes?.unresolved) ? planRes.unresolved : []);
  };

  // Matching a guide show to its stored order.
  const oneshotFor = (show) =>
    selected.find(
      (s) =>
        s.type === "oneshot" &&
        s.label === show.name &&
        s.startTime === show.startTime &&
        s.endTime === show.endTime,
    ) || null;

  const recurringFor = (show) => {
    const { weekday, hhmm } = israelParts(show.startTime);
    return (
      selected.find(
        (s) =>
          s.type === "recurring" &&
          s.active !== false &&
          Array.isArray(s.days) &&
          s.days.includes(weekday) &&
          s.startLocal === hhmm,
      ) || null
    );
  };

  const handleShowClick = (show) => {
    if (show.startTime < Date.now()) {
      addToaster({ message: text.errors.showAlreadyStarted, severity: "warning" });
      return;
    }
    setDialogShow(show);
  };

  const withBusy = async (fn, successMsg, failMsg) => {
    setBusy(true);
    try {
      await fn();
      await refetchSelected();
      addToaster({ message: successMsg, severity: "success" });
      setDialogShow(null);
    } catch {
      addToaster({ message: failMsg, severity: "error" });
    } finally {
      setBusy(false);
    }
  };

  const recordOnce = () =>
    withBusy(
      () =>
        createReshetRecord({
          type: "oneshot",
          name: dialogShow.name,
          description: dialogShow.description,
          startTime: dialogShow.startTime,
          endTime: dialogShow.endTime,
        }),
      text.success.saved,
      text.errors.saveFailed,
    );

  const recordRecurring = (days, allowReruns = false) => {
    const { hhmm } = israelParts(dialogShow.startTime);
    const durationMin = Math.max(
      1,
      Math.round((dialogShow.endTime - dialogShow.startTime) / 60000),
    );
    return withBusy(
      () =>
        createReshetRecord({
          type: "recurring",
          name: dialogShow.name,
          description: dialogShow.description,
          days,
          startLocal: hhmm,
          durationMin,
          allowReruns,
        }),
      text.success.recurringSaved,
      text.errors.saveFailed,
    );
  };

  const removeOrder = (id) =>
    withBusy(
      () => deleteReshetRecord(id),
      text.success.removed,
      text.errors.removeFailed,
    );

  // Multi-select: record a contiguous run as oneshots — the resolver coalesces them
  // into one continuous recording (split at show boundaries if it exceeds 2.5h).
  const recordSelection = async (sels) => {
    const future = (sels || []).filter((s) => s.startTime >= Date.now());
    if (!future.length) {
      addToaster({ message: text.errors.showAlreadyStarted, severity: "warning" });
      return;
    }
    setBusy(true);
    try {
      for (const s of future) {
        await createReshetRecord({
          type: "oneshot",
          name: s.name,
          description: s.description,
          startTime: s.startTime,
          endTime: s.endTime,
        });
      }
      await refetchSelected();
      addToaster({ message: text.success.saved, severity: "success" });
    } catch {
      addToaster({ message: text.errors.saveFailed, severity: "error" });
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <StreamSkeleton />;

  return (
    <CommonPage title={text.title} subtitle={text.subtitle}>
      <Grid2 container spacing={2}>
        <Grid2 size={12}>
          <M3U8Player
            src={MOCK_STREAM_URL}
            width="100%"
            height={isMobile ? 220 : 400}
            autoplay={false}
          />
        </Grid2>
        <Grid2 size={12}>
          {guideUnavailable ? (
            <Alert severity="warning">{text.errors.guideUnavailable}</Alert>
          ) : (
            <TVGuide
              width="100%"
              height={isMobile ? 300 : 360}
              shows={shows}
              selected={selected}
              plan={plan}
              unresolved={unresolved}
              onShowClick={handleShowClick}
              onSelectionRecord={recordSelection}
            />
          )}
        </Grid2>
      </Grid2>

      <RecordDialog
        open={!!dialogShow}
        show={dialogShow}
        oneshot={dialogShow ? oneshotFor(dialogShow) : null}
        recurring={dialogShow ? recurringFor(dialogShow) : null}
        busy={busy}
        text={text}
        onClose={() => setDialogShow(null)}
        onRecordOnce={recordOnce}
        onRecordRecurring={recordRecurring}
        onRemove={removeOrder}
      />
    </CommonPage>
  );
}
